import { useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import useFetch, { Status } from '../../../../../hooks/fetch.hook';
import type MunicipalityApiModel from '../../../models/municipality.api-model';
import Spinner from '../../../../Shared/Spinner';
import Alert from '../../../../Shared/Alert';
import type { UserApiModel } from '../../../models/user.api-model';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../../validation/validation-info';
import type IValidatable from '../../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../../../helpers/validation.helper';
import ValidationSummary from '../../../../Shared/ValidationSummary';
import AntiforgeryService from '../../../../../services/anti-forgery.service';

function Municipality() {
  // === PARAMS ===
  const { municipalityId } = useParams();

  // === REFS ===
  const addAdministratorEmailRef = useRef<IValidatable>(null!);

  // === STATE ===
  const [addAdministratorEmail, setAddAdministratorEmail] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [addAdministratorStatus, setAddAdministratorStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [removeAdministratorStatus, setRemoveAdministratorStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });
  const [deleteMunicipalityStatus, setDeleteMunicipalityStatus] = useState<{ status: Status; statusCode: number }>({ status: Status.notperformed, statusCode: 0 });

  // === FETCH DATA ===
  const [{ status: getMunicipalityStatus, data: getMunicipalityData, httpResponseStatusCode: getMunicipalityHttpResponseStatusCode }] = useFetch<MunicipalityApiModel>(
    `/api/paegnaben/admin/municipalities/${municipalityId}`,
    !municipalityId,
  );
  const [getAdministratorsResult, setGetAdministratorsResult] = useFetch<UserApiModel[]>(`/api/paegnaben/admin/municipalities/${municipalityId}/administrators`);

  // === INTERFACE IMPLEMENTATION ===
  function getValidatableComponents(): ValidatableComponent[] {
    const result: ValidatableComponent[] = [{ component: addAdministratorEmailRef.current, validationInfo: addAdministratorEmail.validationInfo, setState: setAddAdministratorEmail }];

    return result;
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
  }

  // === EVENT HANDLERS ===
  async function handleAddAdministratorButtonClick(): Promise<void> {
    const response = await fetch(`/api/paegnaben/admin/municipalities/${municipalityId}/administrators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
      body: JSON.stringify({ emailAddress: addAdministratorEmail.value }),
    });
    setAddAdministratorStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
    if (response.ok) {
      const addedAdministrator = (await response.json()) as UserApiModel;
      setAddAdministratorEmail({ value: '', validationInfo: new ValidationInfo() });
      setGetAdministratorsResult((ps) => ({ ...ps, data: [...(ps?.data ?? []), addedAdministrator] }));
    } else if (response.status === 400) {
      handleServerValidationErrors((await response.json()).errors);
    }
  }

  async function handleRemoveAdministratorButtonClick(userId: number): Promise<void> {
    const response = await fetch(`/api/paegnaben/admin/municipalities/${municipalityId}/administrators/${userId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
    });
    setRemoveAdministratorStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
    if (response.ok) {
      setGetAdministratorsResult((ps) => ({ ...ps, data: ps.data?.filter((a) => a.id !== userId) }));
    }
  }

  async function handleRemovePendingAdministratorButtonClick(emailAddress: string): Promise<void> {
    const response = await fetch(`/api/paegnaben/admin/municipalities/${municipalityId}/administrators/pending?emailAddress=${encodeURIComponent(emailAddress)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
    });
    setRemoveAdministratorStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
    if (response.ok) {
      setGetAdministratorsResult((ps) => ({ ...ps, data: ps.data?.filter((a) => a.emailAddress !== emailAddress) }));
    }
  }

  async function handleDeleteMunicipalityButtonClick(): Promise<void> {
    const response = await fetch(`/api/paegnaben/admin/municipalities/participating/${municipalityId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
    });
    setDeleteMunicipalityStatus({ status: response.ok ? Status.success : Status.failed, statusCode: response.status });
    if (response.ok) {
      window.location.href = '/admin/kommuner';
    }
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());
  const administrators = getAdministratorsResult.data?.filter((x) => x.id > 0);
  const pendingAdministrators = getAdministratorsResult.data?.filter((x) => x.id < 1);

  // === RENDER ===
  if (getMunicipalityStatus === Status.failed && getMunicipalityHttpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getAdministratorsResult.status === Status.failed && getAdministratorsResult.httpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (removeAdministratorStatus.status === Status.failed && removeAdministratorStatus.statusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (addAdministratorStatus.status === Status.failed && addAdministratorStatus.statusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getMunicipalityStatus === Status.failed && getMunicipalityHttpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (getAdministratorsResult.status === Status.failed && getAdministratorsResult.httpResponseStatusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (removeAdministratorStatus.status === Status.failed && removeAdministratorStatus.statusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (addAdministratorStatus.status === Status.failed && addAdministratorStatus.statusCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <div>
      {getMunicipalityStatus === Status.loading && <Spinner className="my-4 my-md-5" />}
      {getMunicipalityStatus === Status.failed && <Alert show={true} heading={`Kunde inte hämta kommunen med id '${municipalityId}'`} closable={false} />}
      {(!municipalityId || getMunicipalityStatus === Status.success) && getMunicipalityData && (
        <div>
          <h1>{getMunicipalityData.name}</h1>
          {getAdministratorsResult.status === Status.loading && <Spinner />}
          {getAdministratorsResult.status === Status.failed && <Alert show={true} heading="Kunde inte hämta administratörer" closable={false} />}
          {getAdministratorsResult.status === Status.success && (
            <>
              <h2>Kommunadministratörer</h2>
              {administrators && administrators.length === 0 && <p>Inga administratörer</p>}
              {administrators && administrators.length > 0 && (
                <>
                  <ul className="list-unstyled mb-2">
                    {administrators.map((administrator) => (
                      <li key={administrator.id} className="d-flex align-items-center">
                        <span className="me-1 flex-grow-1 text-truncate">{administrator.emailAddress}</span>
                        <button className="btn btn-link px-1" onClick={() => handleRemoveAdministratorButtonClick(administrator.id)}>
                          <span className="text-nowrap flex-grow-0">Ta bort</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <h2 className="mb-0">Kommunadministratörer som ej finns i systemet än</h2>
              <p>Användarna är registrerade som administratörer men har ännu inte skapat ett konto och loggat in</p>
              {pendingAdministrators && pendingAdministrators.length === 0 && <p>Inga avvaktande administratörer</p>}
              {pendingAdministrators && pendingAdministrators.length > 0 && (
                <>
                  <ul className="list-unstyled mb-3">
                    {pendingAdministrators.map((administrator) => (
                      <li key={administrator.emailAddress} className="d-flex align-items-center">
                        <span className="me-1 flex-grow-1 text-truncate">{administrator.emailAddress}</span>
                        <button className="btn btn-link px-1" onClick={() => handleRemovePendingAdministratorButtonClick(administrator.emailAddress)}>
                          <span className="text-nowrap flex-grow-0">Ta bort</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {removeAdministratorStatus.status === Status.failed && (
            <Alert show={true} heading="Kunde inte ta bort administratör" closable={false} className="mb-2" />
          )}
          <form id="form" className="">
            <div className="row">
              <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
              <TextFieldFormGroup
                ref={addAdministratorEmailRef}
                inputId="addAdministartorEmail"
                serverValidationName="emailAddress"
                labelText="E-post"
                className="col-md-6"
                state={[addAdministratorEmail, setAddAdministratorEmail]}
              />
            </div>
            <button type="button" className="btn btn-primary" onClick={handleAddAdministratorButtonClick}>
              Lägg till administratör
            </button>
            {addAdministratorStatus.status === Status.failed && addAdministratorStatus.statusCode !== 400 && (
              <Alert show={true} heading="Kunde inte lägga till administratör" closable={false} className="mt-2" />
            )}
          </form>
          <hr />
          <div>
            <button type="button" className="btn btn-danger" onClick={handleDeleteMunicipalityButtonClick}>
              Ta bort kommun
            </button>
            {deleteMunicipalityStatus.status === Status.failed && deleteMunicipalityStatus.statusCode === 409 && (
              <Alert
                show={true}
                heading="Det gick inte att ta bort kommunen, kontrollera så att det inte finns några administratörer kvar"
                closable={false}
                className="mt-2"
              />
            )}
            {deleteMunicipalityStatus.status === Status.failed && deleteMunicipalityStatus.statusCode !== 409 && (
              <Alert show={true} heading="Det gick inte att ta bort kommunen" closable={false} className="mt-2" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Municipality;